import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react'
import { useLocation } from 'react-router-dom'
import {
  acknowledgeConsoleAlarm,
  getConsoleAlarms,
  getConsoleBusinesses,
  getConsoleQueue,
  type ConsoleAlarm,
  type ConsoleBusiness,
  type ConsoleQueueItem,
} from '../lib/api'
import { loadReason, unacknowledgedCount } from '../lib/consoleStatus'
import { ConsoleShell } from '../components/console/ConsoleShell'
import { ConsoleBusinesses } from '../components/console/ConsoleBusinesses'
import { ConsoleWaiting } from '../components/console/ConsoleWaiting'
import { ConsoleAlarms } from '../components/console/ConsoleAlarms'

// The SOC lead console (BRIEF-F1b; Steve 3 Nov 2026: "yes please we need a
// console"). The page a staff member lands on after signing in: whether every
// business was read, what is waiting on whom, and the platform alarms. Each
// section is one /hub/console/ route and loads, fails and empties on its own.

interface Loaded<T> {
  data: T | null
  error: string | null
}

const loading = { data: null, error: null } as const

function useLoad<T>(load: () => Promise<T>): [Loaded<T>, Dispatch<SetStateAction<Loaded<T>>>] {
  const [state, setState] = useState<Loaded<T>>(loading)
  useEffect(() => {
    let cancelled = false
    load()
      .then((data) => !cancelled && setState({ data, error: null }))
      .catch((err) => !cancelled && setState({ data: null, error: loadReason(err) }))
    return () => {
      cancelled = true
    }
  }, [load])
  return [state, setState]
}

export function ConsolePage({ now }: { now?: Date } = {}) {
  const location = useLocation()
  const [businesses] = useLoad<ConsoleBusiness[]>(getConsoleBusinesses)
  const [queue] = useLoad<ConsoleQueueItem[]>(getConsoleQueue)
  const [alarms, setAlarms] = useLoad<ConsoleAlarm[]>(getConsoleAlarms)
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set())
  const [ackErrors, setAckErrors] = useState<Record<string, string>>({})
  const [clock] = useState(() => now ?? new Date())

  const acknowledge = useCallback(
    async (id: string) => {
      setPending((p) => new Set(p).add(id))
      setAckErrors(({ [id]: _drop, ...rest }) => rest)
      try {
        const updated = await acknowledgeConsoleAlarm(id)
        setAlarms((prev) => ({
          data: (prev.data ?? []).map((a) => (a.id === id ? updated : a)),
          error: prev.error,
        }))
      } catch (err) {
        setAckErrors((e) => ({ ...e, [id]: loadReason(err) }))
      } finally {
        setPending((p) => {
          const next = new Set(p)
          next.delete(id)
          return next
        })
      }
    },
    [setAlarms],
  )

  // The bell and the nav link to a section by hash; bring it into view once
  // the sections have painted.
  const settled = businesses.data !== null || businesses.error !== null
  useEffect(() => {
    if (!location.hash || !settled) return
    const el = document.getElementById(location.hash.slice(1))
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }, [location.hash, settled])

  const alarmCount = alarms.data ? unacknowledgedCount(alarms.data) : null

  return (
    <ConsoleShell alarmCount={alarmCount}>
      <ConsoleBusinesses businesses={businesses.data} error={businesses.error} />
      <ConsoleWaiting items={queue.data} error={queue.error} now={clock} />
      <ConsoleAlarms
        alarms={alarms.data}
        error={alarms.error}
        onAcknowledge={(id) => void acknowledge(id)}
        pendingIds={pending}
        ackErrors={ackErrors}
      />
    </ConsoleShell>
  )
}
